import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchPiecesForProduct } from './api';
import { EditPieceStatusModal, getAvailableTargetStatuses } from './EditPieceStatusModal';
import css from './PiecesTable.css';
import { NamedLink } from '../../components';

const PAGE_SIZE = 5;
const ACTIVE_STATES = 'available,rent,internal-use,service,transfer';

const getStatusInfo = (state) => {
  switch (state) {
    case 'available':
      return { label: 'Dostępny', badgeClass: css.statusAvailable };
    case 'rent':
      return { label: 'Wypożyczony', badgeClass: css.statusRented };
    case 'internal-use':
      return { label: 'Rezerwacja wewnętrzna', badgeClass: css.statusReserved };
    case 'service':
      return { label: 'Serwis', badgeClass: css.statusMaintenance };
    case 'damaged':
      return { label: 'Zniszczony', badgeClass: css.statusDestroyed };
    case 'stolen':
      return { label: 'Kradzież', badgeClass: css.statusStolen };
    case 'sold':
      return { label: 'Sprzedane 2nd life', badgeClass: css.statusSold };
    case 'transfer':
      return { label: 'Transfer', badgeClass: css.statusTransfer };
    default:
      return { label: 'Dostępny', badgeClass: css.statusAvailable };
  }
};

export const PiecesTable = ({
  listingId,
  sdk,
  userPermissions,
  providers,
  currentProvider,
  onAddStock,
}) => {
  const [selectedPieceForEdit, setSelectedPieceForEdit] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);

  const { data: responseData, isLoading: isPiecesLoading, isFetching } = useQuery({
    queryKey: ['pieces', listingId, currentPage, showAll],
    queryFn: () => {
      const params = {
        page: currentPage,
        per_page: PAGE_SIZE,
      };

      if (!showAll) {
        params.state = ACTIVE_STATES;
      }

      return fetchPiecesForProduct(sdk, listingId, params);
    },
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true
  });

  const piecesList = responseData?.data || [];
  const meta = responseData?.meta || {};

  const totalItems = meta.totalCount ?? meta.totalItems ?? piecesList.length;
  const totalPages = meta.totalPages ? Math.ceil(totalItems / PAGE_SIZE) : 1;

  const handleToggleShowAll = (e) => {
    setShowAll(e.target.checked);
    setCurrentPage(1);
  };

  const handlePageChange = (e, newPage) => {
    e.stopPropagation();
    e.preventDefault();
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleOpenEditModal = (e, piece) => {
    e.stopPropagation();
    setSelectedPieceForEdit(piece);
    setIsEditModalOpen(true);
  };

  return (
    <div className={css.itemsTableWrapper}>
      {isFetching && !isPiecesLoading && (
        <div className={css.tableOverlay}>
          <div className={css.spinner} />
        </div>
      )}

      <div className={css.tableHeaderControls}>
        <label className={css.checkboxLabel}>
          <input
            type="checkbox"
            checked={showAll}
            onChange={handleToggleShowAll}
            className={css.checkboxInput}
          />
          <span>Pokaż wszystkie (w tym nieaktywne)</span>
        </label>
      </div>

      {isPiecesLoading ? (
        <div className={css.loadingText}>Ładowanie egzemplarzy...</div>
      ) : (
        <table className={css.itemsTable}>
          <thead>
            <tr>
              <th>Numer seryjny</th>
              <th>Status</th>
              <th className={css.actionsHeader}>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {piecesList.map((piece) => {
              const pieceAttrs = piece.attributes || {};
              const pieceState = pieceAttrs.state;
              const statusInfo = getStatusInfo(pieceState);
              const availableTargets = getAvailableTargetStatuses(pieceState, userPermissions);
              const canEditStatus = availableTargets.length > 0;
              const pieceId = piece.id?.uuid || piece.id;

              return (
                <tr key={pieceId}>
                  <td className={css.serialNumber}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{pieceAttrs.code || 'Brak numeru'}</span>
                      <NamedLink
                        name="PieceDetailsPage"
                        params={{pieceId: pieceId}}>
                          X
                        </NamedLink>
                    </div>
                  </td>
                  <td>
                    <span className={`${css.statusBadge} ${statusInfo.badgeClass}`}>
                      {statusInfo.label}
                    </span>
                  </td>
                  <td className={css.actionsCell}>
                    {canEditStatus && (
                      <button
                        type="button"
                        className={css.editStatusBtn}
                        onClick={(e) => handleOpenEditModal(e, piece)}
                      >
                        Zmień status
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {piecesList.length === 0 && (
              <tr>
                <td colSpan={3} className={css.noResults}>
                  {showAll ? 'Brak dodanych sztuk' : 'Brak aktywnych sztuk'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div className={css.tableFooterControls}>
        <button type="button" className={css.addItemButton} onClick={onAddStock}>
          + Dodaj sztukę
        </button>

        {totalPages > 1 && (
          <div className={css.paginationContainer}>
            <span className={css.paginationInfo}>
              {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, totalItems)} z {totalItems}
            </span>

            <div className={css.paginationNav}>
              <button
                type="button"
                className={css.pageBtn}
                disabled={currentPage === 1 || isFetching}
                onClick={(e) => handlePageChange(e, currentPage - 1)}
                title="Poprzednia strona"
              >
                ‹
              </button>
              <span className={css.pageCounter}>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                className={css.pageBtn}
                disabled={currentPage === totalPages || isFetching}
                onClick={(e) => handlePageChange(e, currentPage + 1)}
                title="Następna strona"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      <EditPieceStatusModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        piece={selectedPieceForEdit}
        sdk={sdk}
        userPermissions={userPermissions}
        providers={providers}
        currentProvider={currentProvider}
      />
    </div>
  );
};